import { Coin } from '@repo/studio-design-system-ui-coin'

import { Button } from '@repo/studio-design-system-ui-button'
import { CheckCircleIcon } from '@repo/studio-design-system-ui-icons/general/check-circle'
import { LFS_URL } from '@repo/studio-utils/config/env'
import { useCoinTransferAnimation } from '@repo/studio-hooks-coins/hooks/use-coin-transfer-animation'
import { useOptimisticUpdateUserBalance } from '@repo/studio-hooks-coins/hooks/use-optimistic-update-user-balance'
import * as React from 'react'
import { useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { tw } from 'utils/classnames'
import { SuccessIndicator } from '../success-indicator'
import {
  buildStatusDialogsResultFromPaymentStatus,
  openPaymentRequiredDialog,
} from '@repo/studio-hooks-payments/use-payment-required-dialog'

import { PaymentStatus, TrialActivationReason } from '@repo/graphql-types'
import { usePaymentsActivateTrialMutation } from 'payments/gen/stripe'
import { getActivateTrialPaymentResult } from 'payments/lib/activate-trial/get-activate-trial-payment-result'
import { useIsCompactViewport } from '@repo/studio-hooks-dom/use-breakpoint'
import { useConfirmTrialActivation } from '@repo/studio-hooks-coins/use-confirm-trial-activation'
import { useQueryClient } from '@tanstack/react-query'
import { UPGRADE_SOURCES } from 'payments/upgrade-sources'
import { userQueryKey, useUserQuery } from 'auth/hooks'
import { AnimatePresence, m, LazyMotion, domAnimation } from 'framer-motion'
import { toast } from '@repo/studio-components-toast'

interface SkipTrialMessageProps {
  className?: string
  show: boolean
}

const COIN_BUNDLE_IMAGE = `${LFS_URL}/studio/coins/coins-bundle.webp`

const UPDATE_BALANCE_DELAY = 800
const SHOW_SUCCESS_TIME = 5000

export function SkipTrialMessage({ className, show }: SkipTrialMessageProps) {
  const t = useStudioTranslate()
  const [showSuccessButton, setShowSuccessButton] = React.useState(false)
  const queryClient = useQueryClient()
  const { trigger } = useCoinTransferAnimation()
  const updateCoinBalance = useOptimisticUpdateUserBalance()
  const coinsAmount = 620_000
  const containerRef = React.useRef<HTMLFormElement | null>(null)
  const isCompactViewport = useIsCompactViewport()
  const { data: subscriptionId, isPending: isPendingSubscriptionId } = useUserQuery(undefined, {
    select: data => data.me?.user?.subscriptionAccess.studioAi.subscription?.id,
  })
  const onUpgradeSuccess = React.useCallback(() => {
    setShowSuccessButton(true)
    trigger({ source: containerRef.current })
    setTimeout(() => updateCoinBalance.adjustUserBalance(coinsAmount), UPDATE_BALANCE_DELAY)
    setTimeout(() => setShowSuccessButton(false), SHOW_SUCCESS_TIME)
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: userQueryKey })
    }, SHOW_SUCCESS_TIME)
    if (!isCompactViewport) {
      toast({
        position: 'top-center',
        icon: <CheckCircleIcon />,
        title: t('studio.components.skip_trial_message.toasts.success.title'),
        description: '',
        duration: SHOW_SUCCESS_TIME,
      })
    }
  }, [trigger, updateCoinBalance, coinsAmount, isCompactViewport, t, queryClient])

  const onUpgradeError = React.useCallback(() => {
    toast({
      title: t('studio.components.skip_trial_message.toasts.error_generic.title'),
      description: t('studio.components.skip_trial_message.toasts.error_generic.description'),
    })
  }, [t])

  const confirmTrialActivation = useConfirmTrialActivation()
  const activateTrialMutation = usePaymentsActivateTrialMutation({
    onSuccess: data => {
      const result = getActivateTrialPaymentResult(data.paymentsActivateTrial)

      if (result.paymentStatus !== PaymentStatus.Succeeded) {
        const dialogStatusResult = buildStatusDialogsResultFromPaymentStatus(
          result.paymentStatus,
          result.hostedUrl,
        )
        openPaymentRequiredDialog(dialogStatusResult)
      } else {
        onUpgradeSuccess()
      }
    },
    onError: onUpgradeError,
  })

  const handleClick = React.useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (!subscriptionId) {
        return
      }
      const confirmed = await confirmTrialActivation({ willActivateTrial: true })
      if (!confirmed) {
        return
      }
      activateTrialMutation.mutate({
        input: {
          id: subscriptionId,
          activationReason: TrialActivationReason.UserRequest,
          activationSource: UPGRADE_SOURCES.STUDIO_ALL_LEFT_SIDEBAR,
        },
        applyBonus: false,
      })
    },
    [subscriptionId, activateTrialMutation, confirmTrialActivation],
  )

  const isPending = isPendingSubscriptionId || activateTrialMutation.isPending

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence initial={false}>
        {show ? (
          <SkipTrialCard
            className={className}
            containerRef={containerRef}
            isPending={isPending}
            onSubmit={handleClick}
            showSuccessButton={showSuccessButton}
          />
        ) : null}
      </AnimatePresence>
    </LazyMotion>
  )
}

function SkipTrialCard({
  className,
  containerRef,
  isPending,
  onSubmit,
  showSuccessButton,
}: {
  className?: string
  containerRef: React.RefObject<HTMLFormElement | null>
  isPending: boolean
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  showSuccessButton: boolean
}) {
  const t = useStudioTranslate()
  return (
    <m.form
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.2, delay: 0.2 } }}
      exit={{
        opacity: 0,
        transition: { duration: 0 },
      }}
      className={tw(
        'rounded-2 border-themed-violet-800 bg-themed-violet-1000 relative flex w-full min-w-[216px] cursor-pointer flex-col overflow-hidden border p-3 text-left lg:p-4',
        isPending && 'cursor-wait',
        className,
      )}
      ref={containerRef}
      onSubmit={onSubmit}
    >
      <button
        type="submit"
        disabled={isPending}
        className="absolute inset-0 opacity-0"
        aria-hidden="true"
      />
      <div className="mb-1.5 flex w-full items-center justify-center lg:hidden">
        <Coin />
        <Coin className="-ml-1" />
        <Coin className="-ml-1" />
      </div>
      <h3
        className={tw(
          'max-lg:typography-13 max-lg:w-full max-lg:text-center max-lg:font-bold',
          'typography-24 font-extrabold leading-[24px]',
        )}
      >
        {t('studio.components.skip_trial_message.content.title')}
      </h3>

      <p className="typography-13 mt-1 max-lg:w-full max-lg:text-center">
        {t('studio.components.skip_trial_message.content.copy_1')}
      </p>

      <div className="mt-6 flex justify-center max-lg:hidden">
        <img
          width={180}
          height={120}
          src={COIN_BUNDLE_IMAGE}
          alt={t('studio.components.skip_trial_message.content.image_alt')}
        />
      </div>
      <Button
        loading={isPending}
        variant="primary"
        size="sm"
        type="submit"
        className={tw('mt-5 w-fit max-lg:w-full', isPending && 'cursor-wait')}
        disabled={isPending}
      >
        <AnimatePresence mode="wait" initial={false}>
          {showSuccessButton ? (
            <SuccessIndicator
              label={t('studio.components.skip_trial_message.content.purchase_success')}
            />
          ) : (
            <m.span
              key="default"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { duration: 0.3 } }}
              exit={{ opacity: 0, transition: { duration: 0.2 } }}
            >
              {t('studio.components.skip_trial_message.content.cta')}
            </m.span>
          )}
        </AnimatePresence>
      </Button>

      <p className="typography-11 mt-1 text-gray-200 max-lg:w-full max-lg:text-center">
        {t('studio.components.skip_trial_message.content.copy_2')}
      </p>
    </m.form>
  )
}
